import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useCases } from '../../core/di/useCases';
import { useAuth } from '../../core/providers/AuthProvider';
import { usePreferences } from './usePreferences';
import { GEO } from '../../core/constants/geo';

export const useNearbyPosts = (userLocation, options = {}) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const { user } = useAuth();
  const { get: getPreference } = usePreferences();
  const { excludeUserPosts = false, limit: postLimit = 20, maxDistanceOverride, userInterests = [] } = options;

  // Si maxDistanceOverride est fourni, l'utiliser, sinon lire depuis les préférences
  const maxDistanceKm = useMemo(() => {
    return maxDistanceOverride !== undefined
      ? Math.min(maxDistanceOverride, GEO.MAX_RADIUS_KM)
      : Math.min(getPreference('maxDistance') || GEO.DEFAULT_RADIUS_KM, GEO.MAX_RADIUS_KM);
  }, [getPreference, maxDistanceOverride]);
  
  const mountedRef = useRef(true);
  const isLoadingMoreRef = useRef(false);
  const lastVisibleRef = useRef(null);
  const unsubscribeRef = useRef(null);
  // Garde une trace de la dernière requête pour éviter les doublons
  const lastQueryRef = useRef({ queryKey: null, refreshQueryKey: null, forceRefresh: false });

  // Filtrer les posts selon les intérêts de l'utilisateur
  const filteredPosts = useMemo(() => {
    // Si pas d'intérêts définis, on renvoie tous les posts
    if (!userInterests || !Array.isArray(userInterests) || userInterests.length === 0) {
      return posts;
    }
    
    // Filtrer les posts dont le type d'activité correspond à au moins un intérêt
    const filtered = posts.filter(post => {
      if (!post.activity?.type) return false;
      
      // Vérifier si le type est un objet avec un id ou directement un string
      const activityType = typeof post.activity.type === 'object' 
        ? post.activity.type.id 
        : post.activity.type;
      
      // Vérifier si ce type correspond à un des intérêts de l'utilisateur
      return userInterests.some(interest => 
        interest.toLowerCase() === activityType.toLowerCase()
      );
    });
    
    return filtered;
  }, [posts, userInterests]);

  const convertLocation = (location) => {
    if (!location) return null;
    
    if (typeof location.latitude === 'number' && typeof location.longitude === 'number') {
      return {
        latitude: location.latitude,
        longitude: location.longitude
      };
    }
    
    if (location.coordinates?.latitude && location.coordinates?.longitude) {
      return {
        latitude: location.coordinates.latitude,
        longitude: location.coordinates.longitude
      };
    }
    
    if (location.location?.lat && location.location?.lng) {
      return {
        latitude: location.location.lat,
        longitude: location.location.lng
      };
    }
    
    return null;
  };

  // Nettoyer l'abonnement précédent
  const cleanupSubscription = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
  }, []);

  // Fonction pour établir la requête en temps réel avec le nouveau use case
  useEffect(() => {
    // Si on n'a pas de localisation, pas besoin de faire une requête
    if (!userLocation) {
      cleanupSubscription();
      setLoading(false);
      return;
    }

    const formattedLocation = convertLocation(userLocation);
    if (!formattedLocation) {
      cleanupSubscription();
      setLoading(false);
      return;
    }

    // SIMPLIFICATION: Supprimer la logique de cache de requête qui cause la boucle
    
    // Nettoyer l'abonnement précédent avant d'en créer un nouveau
    cleanupSubscription();
    
    setLoading(true);
    console.log(`Établissement d'une requête en temps réel - rayon: ${maxDistanceKm}km`);

    // Utiliser le nouveau use case pour l'abonnement temps réel
    const setupSubscription = async () => {
      try {
        const unsubscribe = await useCases.subscribeToNearbyPosts.execute({
          center: formattedLocation,
          radiusInKm: maxDistanceKm,
          limit: postLimit,
          excludeUserId: excludeUserPosts && user ? user.uid : null,
          onUpdate: (result) => {
            if (!mountedRef.current) return;
            
            console.log(`📡 Mise à jour reçue: ${result.posts.length} posts`);
            
            // SIMPLIFICATION: Mise à jour directe sans logique complexe
            setPosts(result.posts);
            setHasMore(result.hasMore);
            setLoading(false);
            setError(null);
          },
          onError: (err) => {
            if (!mountedRef.current) return;
            
            console.error("Erreur lors de l'écoute des posts:", err);
            setError(err.message);
            setLoading(false);
          }
        });

        // Stocker l'unsubscribe pour un nettoyage ultérieur
        unsubscribeRef.current = unsubscribe;
      } catch (err) {
        console.error("Erreur lors de l'établissement de l'abonnement:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    setupSubscription();

    // Nettoyage de l'abonnement lors du démontage
    return () => {
      cleanupSubscription();
    };
  }, [userLocation, maxDistanceKm, user, excludeUserPosts, postLimit, cleanupSubscription]);

  // Fonction pour charger plus de posts (pagination)
  const loadMore = useCallback(async () => {
    if (isLoadingMoreRef.current || !mountedRef.current || !hasMore || !lastVisibleRef.current) return;
    
    try {
      isLoadingMoreRef.current = true;
      setLoading(true);

      const formattedLocation = convertLocation(userLocation);
      if (!formattedLocation) return;

      const result = await useCases.listNearbyPosts.execute({
        location: formattedLocation,
        maxDistanceKm,
        limit: postLimit,
        lastVisible: lastVisibleRef.current,
        excludeUserId: excludeUserPosts && user ? user.uid : null
      });

      if (!mountedRef.current) return;

      setPosts(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const uniqueNewPosts = result.posts.filter(p => !existingIds.has(p.id));
        return [...prev, ...uniqueNewPosts];
      });
      
      lastVisibleRef.current = result.lastVisible;
      setHasMore(result.hasMore);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      isLoadingMoreRef.current = false;
    }
  }, [hasMore, userLocation, maxDistanceKm, user, excludeUserPosts, postLimit]);

  // Rafraîchir manuellement (par exemple après création d'un post)
  const refresh = useCallback(async () => {
    if (!userLocation) return;

    try {
      const formattedLocation = convertLocation(userLocation);
      if (!formattedLocation) return;

      console.log(`Rafraîchissement manuel - rayon: ${maxDistanceKm}km`);

      const result = await useCases.listNearbyPosts.execute({
        location: formattedLocation,
        maxDistanceKm,
        limit: postLimit,
        excludeUserId: excludeUserPosts && user ? user.uid : null,
        refresh: true
      });
      
      // SIMPLIFICATION: Remplacement direct
      setPosts(result.posts);
      setHasMore(result.hasMore);
      
    } catch (err) {
      console.error("Erreur lors du refresh:", err);
    }
  }, [userLocation, maxDistanceKm, user, excludeUserPosts, postLimit]);

  // Forcer un rafraîchissement même si les paramètres sont identiques
  const forceRefresh = useCallback(async () => {
    if (lastQueryRef.current) {
      lastQueryRef.current.forceRefresh = true;
    }
    return refresh();
  }, [refresh]);

  // Nettoyage lors du démontage du hook
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      cleanupSubscription();
    };
  }, [cleanupSubscription]);

  const handleToggleParticipation = useCallback(async (postId) => {
    if (!user) return;

    const post = posts.find(p => p.id === postId);
    if (!post?.activity) return;

    const isParticipating = post.activity.participants?.includes(user.uid);

    // Mise à jour optimiste de l'UI
    setPosts(prev => prev.map(p => {
      if (p.id !== postId || !p.activity) return p;
      
      const participants = p.activity.participants || [];
      const currentParticipants = p.activity.currentParticipants || 0;

      return {
        ...p,
        activity: {
          ...p.activity,
          participants: isParticipating 
            ? participants.filter(id => id !== user.uid)
            : [...participants, user.uid],
          currentParticipants: isParticipating 
            ? currentParticipants - 1 
            : currentParticipants + 1
        }
      };
    }));

    try {
      if (isParticipating) {
        await useCases.leaveActivity.execute(postId, user.uid);
      } else {
        await useCases.joinActivity.execute({
          postId,
          userId: user.uid,
          participantData: {
            uid: user.uid,
            name: user.displayName || 'Utilisateur',
            photoURL: user.photoURL || null
          },
          currentParticipants: post.activity.currentParticipants || 0,
          currentDetails: post.activity.participantsDetails || []
        });
      }
    } catch (error) {
      // Annuler la mise à jour optimiste en cas d'erreur
      setPosts(prev => prev.map(p => {
        if (p.id !== postId || !p.activity) return p;
        
        const participants = p.activity.participants || [];
        const currentParticipants = p.activity.currentParticipants || 0;

        return {
          ...p,
          activity: {
            ...p.activity,
            participants: isParticipating 
              ? [...participants, user.uid]
              : participants.filter(id => id !== user.uid),
            currentParticipants: isParticipating 
              ? currentParticipants + 1 
              : currentParticipants - 1
          }
        };
      }));
      console.error('Error toggling participation:', error);
      setError(error.message);
    }
  }, [user, posts]);

  // Supprimer un post
  const deletePost = useCallback(async (postId) => {
    if (!user?.uid) throw new Error('You must be logged in to delete posts');
    
    try {
      setLoading(true);
      await useCases.deletePost.execute(postId, user.uid);
      
      // Mise à jour optimiste de l'UI
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch (error) {
      console.error('Error deleting post:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  // Renvoyer les posts filtrés par intérêts au lieu des posts bruts
  return {
    posts: filteredPosts,
    allPosts: posts, // Au cas où on a besoin des posts non filtrés
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    forceRefresh,
    toggleParticipation: handleToggleParticipation,
    deletePost,
    isEmpty: filteredPosts.length === 0 && !loading
  };
};
